import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { certificateAPI, settingsAPI, STORAGE_URL } from '../services/api';
import CertificatePrint from '../components/certificates/CertificatePrint';

const PrintCertificate = () => {
    const { id } = useParams();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [certificate, setCertificate] = useState(null);
    const [settings, setSettings] = useState({
        stamp: null,
        logo: null,
        office_name: '',
        office_name_mr: '',
        office_address: '',
        office_address_mr: '',
        authority_name: '',
        authority_name_mr: '',
        place: '',
        place_mr: '',
        authority_signature: null
    });

    // Get language from navigation state, sessionStorage, or from certificate or default to 'marathi'
    const [language, setLanguage] = useState(() => {
        // Try to get from location state first
        if (location.state?.language) {
            return location.state.language;
        }
        // Try to get from sessionStorage (set by parent window)
        try {
            const storedLang = sessionStorage.getItem('printLanguage');
            if (storedLang) {
                sessionStorage.removeItem('printLanguage'); // Clean up
                return storedLang;
            }
        } catch (e) {
            // sessionStorage might not be available
        }
        // Default to marathi
        return 'marathi';
    });

    useEffect(() => {
        const fetchCertificate = async () => {
            try {
                setLoading(true);

                // Fetch certificate data
                const certResponse = await certificateAPI.get(id);

                // Handle nested data structure
                const certData = certResponse.data.data || certResponse.data;

                console.log('Certificate data loaded:', certData);
                console.log('Certificate response:', certResponse);
                console.log('Photo fields:', {
                    husband_photo: certData.husband_photo,
                    husband_signature: certData.husband_signature,
                    wife_photo: certData.wife_photo,
                    wife_signature: certData.wife_signature,
                    qr_code: certData.qr_code,
                });
                console.log('All certificate fields:', Object.keys(certData));
                console.log('Certificate values:', certData);

                // Set language from certificate if available, default to showing both
                if (certData.language) {
                    // Show both Marathi and English by default (matching backend template)
                    setLanguage('both');
                }

                // Fetch settings
                const settingsResponse = await settingsAPI.get();
                const settingsData = {
                    stamp: settingsResponse.data.stamp ? `${STORAGE_URL}/${settingsResponse.data.stamp}` : null,
                    logo: settingsResponse.data.logo ? `${STORAGE_URL}/${settingsResponse.data.logo}` : null,
                    office_name: settingsResponse.data.office_name || '',
                    office_name_mr: settingsResponse.data.office_name_mr || '',
                    office_address: settingsResponse.data.office_address || '',
                    office_address_mr: settingsResponse.data.office_address_mr || '',
                    authority_name: settingsResponse.data.authority_name || '',
                    authority_name_mr: settingsResponse.data.authority_name_mr || '',
                    place: settingsResponse.data.place || '',
                    place_mr: settingsResponse.data.place_mr || '',
                    authority_signature: settingsResponse.data.authority_signature
                        ? `${STORAGE_URL}/${settingsResponse.data.authority_signature}`
                        : null,
                };

                console.log('Settings loaded:', settingsData);

                // Resolve photo URLs for debugging
                console.log('Resolved Photo URLs:', {
                    husbandPhoto: certData.husband_photo ? `${STORAGE_URL}/${certData.husband_photo}` : null,
                    wifePhoto: certData.wife_photo ? `${STORAGE_URL}/${certData.wife_photo}` : null,
                    husbandSignature: certData.husband_signature ? `${STORAGE_URL}/${certData.husband_signature}` : null,
                    wifeSignature: certData.wife_signature ? `${STORAGE_URL}/${certData.wife_signature}` : null,
                });

                // Merge settings with certificate data for transliteration
                const mergedData = {
                    ...certData,
                    office_name: certData.office_name || settingsData.office_name,
                    office_name_mr: certData.office_name_mr || settingsData.office_name_mr,
                    office_address: certData.office_address || settingsData.office_address,
                    office_address_mr: certData.office_address_mr || settingsData.office_address_mr,
                    authority_name: certData.authority_name || settingsData.authority_name,
                    authority_name_mr: certData.authority_name_mr || settingsData.authority_name_mr,
                    place: certData.place || settingsData.place,
                    place_mr: certData.place_mr || settingsData.place_mr,
                };

                console.log('Merged certificate data:', mergedData);

                setCertificate(mergedData);
                setSettings(settingsData);

                // Auto-trigger print after content loads
                setTimeout(() => {
                    window.print();
                }, 500);

            } catch (err) {
                console.error('Error fetching certificate:', err);
                console.error('Error details:', err.response?.data);
                setError('Failed to load certificate');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchCertificate();
        }
    }, [id, language]);

    // Print-specific styles with Devanagari font support
    const printStyles = `
        /* Load Noto Sans Devanagari font for Marathi text */
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap');

        /* Global font family for certificate */
        .certificate-container,
        .certificate-container * {
            font-family: 'Noto Sans Devanagari', 'NotoSansDevanagari', sans-serif !important;
        }

        @media print {
            @page {
                size: A4 portrait;
                margin: 3mm;
            }

            /* iOS Safari specific fixes */
            @supports (-webkit-touch-callout: none) {
                @page {
                    size: A4 portrait;
                    margin: 2mm;
                }
            }

            html, body {
                margin: 0 !important;
                padding: 0 !important;
                height: auto !important;
                min-height: auto !important;
                background: white !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                overflow: visible !important;
            }

            /* Hide non-print elements */
            .no-print,
            .no-print * {
                display: none !important;
            }

            /* Ensure certificate takes full page - iOS fix */
            .print-container {
                width: 100% !important;
                max-width: none !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow: visible !important;
                height: auto !important;
            }

            /* Certificate wrapper - fit to single page */
            .certificate-container {
                width: 100% !important;
                max-width: none !important;
                margin: 0 !important;
                padding: 0 !important;
                page-break-inside: avoid !important;
                page-break-before: avoid !important;
                page-break-after: avoid !important;
                overflow: hidden !important;
                height: auto !important;
            }

            /* Preserve fonts and colors */
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
                box-sizing: border-box !important;
            }

            /* Ensure Devanagari font renders correctly in print */
            .certificate-container,
            .certificate-container * {
                font-family: 'Noto Sans Devanagari', 'NotoSansDevanagari', sans-serif !important;
            }

            /* Prevent page breaks inside elements */
            div, table, tbody, tr, td, th, p, span {
                page-break-inside: avoid !important;
                -webkit-region-break-inside: avoid !important;
                break-inside: avoid !important;
            }

            /* Ensure images print correctly */
            img {
                max-width: 100% !important;
                page-break-inside: avoid !important;
                -webkit-region-break-inside: avoid !important;
            }

            /* Preserve borders and backgrounds */
            div, table, td, th {
                border-collapse: collapse;
            }

            /* iOS WebKit specific - reduce content height */
            @supports (-webkit-overflow-scrolling: touch) {
                .certificate-container {
                    transform: scale(0.98);
                    transform-origin: top left;
                }
            }
        }

        /* Screen styles */
        @media screen {
            body {
                background: #f5f5f5;
                padding: 20px;
            }

            .print-container {
                max-width: 794px;
                margin: 0 auto;
                background: white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
        }

        /* Loading and error states */
        .loading-container,
        .error-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            padding: 20px;
        }

        .loading-spinner {
            width: 50px;
            height: 50px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #3498db;
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }

        /* Mobile: Show certificate at desktop size - users can zoom/scroll */
        @media screen and (max-width: 768px) {
            .certificate-container {
                /* Maintain desktop dimensions - don't scale down */
                min-width: 794px !important;
            }

            /* Ensure print container allows horizontal scroll */
            .print-container {
                overflow-x: auto !important;
                -webkit-overflow-scrolling: touch !important;
            }
        }

    `;

    if (loading) {
        return (
            <>
                <style>{printStyles}</style>
                <div className="loading-container no-print">
                    <div className="loading-spinner"></div>
                    <p style={{ marginTop: '20px', fontSize: '16px', color: '#666' }}>
                        Loading certificate...
                    </p>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <style>{printStyles}</style>
                <div className="error-container no-print">
                    <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <h2 style={{ color: '#e74c3c', marginBottom: '16px' }}>Error</h2>
                        <p style={{ color: '#666', marginBottom: '20px' }}>{error}</p>
                        <button
                            onClick={() => window.history.back()}
                            style={{
                                padding: '10px 20px',
                                background: '#3498db',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <style>{printStyles}</style>

            {/* Screen-only controls */}
            <div className="no-print" style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 1000,
                display: 'flex',
                gap: '10px'
            }}>
                <button
                    onClick={() => window.print()}
                    style={{
                        padding: '10px 20px',
                        background: '#2ecc71',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                >
                    🖨️ Print Certificate
                </button>
                <button
                    onClick={() => window.history.back()}
                    style={{
                        padding: '10px 20px',
                        background: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                >
                    ← Back
                </button>
            </div>

            {/* Certificate Content */}
            <div className="print-container certificate-container">
                <CertificatePrint
                    data={certificate}
                    photos={{
                        husbandPhoto: certificate?.husband_photo,
                        husbandSignature: certificate?.husband_signature,
                        wifePhoto: certificate?.wife_photo,
                        wifeSignature: certificate?.wife_signature,
                    }}
                    language={language}
                    stamp={settings?.stamp}
                    logo={settings?.logo}
                    authoritySignature={settings?.authority_signature}
                />
            </div>
        </>
    );
};

export default PrintCertificate;